import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import withTranslate from 'containers/widgets/withTranslate';
import Button from 'components/atoms/Button';
import Icon, { IconSizes, IconVariants } from 'components/foundations/Icon';
import { formatVersionDate, formatVersionDateLong } from '../utils/dateFormat';
import css from './VersionsShelf.css';

const VERSIONS_SHELF_PREFIX = 'STATIC.PAGES.AI_CONTEXT.VERSIONS_SHELF';

const VersionRow = ({
    version,
    isCurrent,
    isPreviewing,
    isLoadingThisPreview,
    isRestoringThis,
    isAnyPreviewLoading,
    isAnyRestoring,
    onPreview,
    onRestore,
    translate,
}) => {
    const dateLabel = formatVersionDate(version.created);
    const creatorLabel = version.creator_email || translate(`${VERSIONS_SHELF_PREFIX}.UNKNOWN_USER`);
    return (
        <li className={classNames(css.row, { [css.currentRow]: isCurrent })}>
            <div className={css.rowMeta}>
                <div className={css.rowHeader}>
                    <span className={css.rowDate} title={formatVersionDateLong(version.created)}>
                        {dateLabel}
                    </span>
                    {isCurrent && (
                        <span className={classNames(css.badge, css.currentBadge)}>
                            {translate(`${VERSIONS_SHELF_PREFIX}.CURRENT`)}
                        </span>
                    )}
                    {version.source === 'restore' && (
                        <span className={css.badge}>
                            <Icon
                                name="history"
                                size={IconSizes.XSMALL}
                                variant={IconVariants.DARK}
                                interactive={false}
                            />
                            {translate(`${VERSIONS_SHELF_PREFIX}.RESTORED`)}
                        </span>
                    )}
                </div>
                <div className={css.rowCreator}>{creatorLabel}</div>
            </div>
            {!isCurrent && (
                <div className={css.rowActions}>
                    <span
                        title={translate(`${VERSIONS_SHELF_PREFIX}.PREVIEW_TOOLTIP`, {
                            dateLabel,
                            creatorLabel,
                        })}
                    >
                        <Button
                            type={isPreviewing ? 'primary' : 'secondary'}
                            onClick={() => onPreview(version)}
                            showSpinner={isLoadingThisPreview}
                            disabled={(isAnyPreviewLoading && !isLoadingThisPreview) || isAnyRestoring}
                        >
                            {translate(
                                isPreviewing
                                    ? `${VERSIONS_SHELF_PREFIX}.PREVIEWING`
                                    : `${VERSIONS_SHELF_PREFIX}.PREVIEW`
                            )}
                        </Button>
                    </span>
                    <span
                        title={translate(`${VERSIONS_SHELF_PREFIX}.RESTORE_TOOLTIP`, {
                            dateLabel,
                            creatorLabel,
                        })}
                    >
                        <Button
                            type="secondary"
                            onClick={() => onRestore(version)}
                            showSpinner={isRestoringThis}
                            disabled={(isAnyRestoring && !isRestoringThis) || isAnyPreviewLoading}
                        >
                            {translate(`${VERSIONS_SHELF_PREFIX}.RESTORE`)}
                        </Button>
                    </span>
                </div>
            )}
        </li>
    );
};

VersionRow.propTypes = {
    version: PropTypes.shape({
        id: PropTypes.number.isRequired,
        created: PropTypes.string,
        creator_email: PropTypes.string,
        source: PropTypes.string,
    }).isRequired,
    isCurrent: PropTypes.bool.isRequired,
    isPreviewing: PropTypes.bool.isRequired,
    isLoadingThisPreview: PropTypes.bool.isRequired,
    isRestoringThis: PropTypes.bool.isRequired,
    isAnyPreviewLoading: PropTypes.bool.isRequired,
    isAnyRestoring: PropTypes.bool.isRequired,
    onPreview: PropTypes.func.isRequired,
    onRestore: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
};

export default withTranslate(VersionRow);
